import React, { useCallback, useEffect, useState } from "react";
import { MessageCircle, Send, Paperclip, ExternalLink, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { whatsappHref } from "@/lib/whatsapp";
import { MAX_FILE_BYTES, DRIVE_HINT, compressImage } from "@/lib/uploadLimits";

export default function MessageCenter() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [recipient, setRecipient] = useState("all");
  const [body, setBody] = useState("");
  const [whatsappBody, setWhatsappBody] = useState("");
  const [directory, setDirectory] = useState([]);
  const [principalWhatsappContacts, setPrincipalWhatsappContacts] = useState([]);
  const [whatsappError, setWhatsappError] = useState("");
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [files, setFiles] = useState([]);
  const canManageContacts = user?.role === "admin" && !user?.scope;
  const load = useCallback(async () => {
    try {
      const [m, c, contactDirectory] = await Promise.all([
        api.get("/messages"),
        api.get("/message-contacts"),
        canManageContacts ? api.get("/contacts") : Promise.resolve({ data: [] }),
      ]);
      const unreadIncoming = m.data.filter((message) => (
        message.sender_id !== user?.id
        && !message.read_by?.includes(user?.id)
        && (message.recipient_id === user?.id || (user?.role === "counselor" && message.broadcast))
      ));
      await Promise.all(unreadIncoming.map((message) => api.post(`/messages/${message.id}/read`)));
      const readIds = new Set(unreadIncoming.map((message) => message.id));
      setMessages(m.data.map((message) => (
        readIds.has(message.id)
          ? { ...message, read_by: [...(message.read_by || []), user.id] }
          : message
      )));
      setContacts(c.data);
      const availableDirectory = user?.role === "counselor" ? c.data : contactDirectory.data;
      setDirectory(availableDirectory);
      const firstEmailContact = availableDirectory.find((contact) => contact.email);
      if (!emailRecipient && firstEmailContact) setEmailRecipient(firstEmailContact.id);
      if (user?.role !== "admin" && c.data[0] && recipient === "all") setRecipient(c.data[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  }, [canManageContacts, emailRecipient, recipient, user?.id, user?.role]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!["principal", "counselor"].includes(user?.role)) return;
    let active = true;
    setPrincipalWhatsappContacts([]);
    setWhatsappError("");
    setWhatsappLoading(true);
    const endpoint = user.role === "principal" ? "/principal/whatsapp-contacts" : "/counselor/whatsapp-contacts";
    api.get(endpoint).then(({ data }) => {
      if (active) setPrincipalWhatsappContacts(data);
    }).catch((failure) => { if (active) setWhatsappError(formatErr(failure)); })
      .finally(() => { if (active) setWhatsappLoading(false); });
    return () => { active = false; };
  }, [user?.id, user?.role]);
  const encodeFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, data: reader.result });
    reader.onerror = reject; reader.readAsDataURL(file);
  });
  const send = async (e) => {
    e.preventDefault();
    try {
      const attachments = await Promise.all(files.map(async (file) => file.type.startsWith("image/") ? { name: file.name.replace(/\.[^.]+$/, "") + ".jpg", type: "image/jpeg", data: await compressImage(file) } : encodeFile(file)));
      const group = ["all", "counselors", "managers"].includes(recipient) ? recipient : null;
      await api.post("/messages", {
        recipient_id: group ? null : recipient,
        recipient_group: group,
        body,
        attachments,
      });
      toast.success("تم إرسال الرسالة"); setBody(""); setFiles([]); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const emailContact = directory.find((contact) => contact.id === emailRecipient);
  const emailParams = new URLSearchParams({
    to: emailContact?.email || "",
    subject: emailSubject,
    body: emailBody,
  });
  const hasEmailRecipient = Boolean(emailContact?.email);
  const gmailHref = hasEmailRecipient
    ? `https://mail.google.com/mail/?view=cm&fs=1&${new URLSearchParams({
      to: emailContact.email,
      su: emailSubject,
      body: emailBody,
    }).toString()}`
    : "";
  const outlookHref = hasEmailRecipient
    ? `https://outlook.office.com/mail/deeplink/compose?${emailParams.toString()}`
    : "";
  const mailtoHref = hasEmailRecipient
    ? `mailto:${emailContact.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`
    : "";
  const whatsappContacts = ["principal", "counselor"].includes(user?.role) ? principalWhatsappContacts : canManageContacts
    ? directory.filter((contact) => contact.phone)
    : [];
  return (
    <div className="space-y-6">
      <div><div className="text-sm text-[#3E6A56] font-medium mb-2">التواصل والمتابعة</div><h1 className="text-3xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>الرسائل والتواصل</h1><p className="text-[#4A554E] mt-2">رسائل جماعية أو خاصة مع إمكانية إرفاق فيديو أو صوت.</p></div>
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <form onSubmit={send} className="bg-white rounded-2xl border border-[#e6e2d8] p-5 space-y-4">
          <h2 className="font-bold text-lg flex items-center gap-2"><MessageCircle className="w-5 h-5 text-[#3E6A56]" /> رسالة جديدة</h2>
          <div><Label className="mb-1.5 block">إلى</Label><Select value={recipient} onValueChange={setRecipient}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{user?.role === "admin" && !user?.scope && <><SelectItem value="all">الجميع (المدراء والمرشدون)</SelectItem><SelectItem value="counselors">المرشدون</SelectItem><SelectItem value="managers">المدراء</SelectItem></>}{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.communication_role || (c.role === "principal" ? "مدير" : c.role === "counselor" ? "مرشد" : c.role === "general_manager" ? "مدير عام" : "مشرف")} — {c.username}</SelectItem>)}</SelectContent></Select></div>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="اكتب ملاحظتك أو رسالتك..." className="min-h-32 rounded-xl" />
          <Input type="file" multiple accept="image/*,video/*,audio/*" onChange={(e) => {
            const picked = Array.from(e.target.files || []);
            if (picked.some((f) => !f.type.startsWith("image/") && f.size > MAX_FILE_BYTES)) { toast.error(DRIVE_HINT); e.target.value = ""; setFiles([]); return; }
            setFiles(picked);
          }} />
          <Button type="submit" className="w-full rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]"><Send className="w-4 h-4 me-2" />إرسال</Button>
        </form>
        <div className="bg-white rounded-2xl border border-[#e6e2d8] p-5"><h2 className="font-bold text-lg mb-4">صندوق الرسائل</h2><div className="space-y-3 max-h-[540px] overflow-y-auto">{messages.length === 0 && <div className="text-sm text-[#4A554E]">لا توجد رسائل حتى الآن.</div>}{messages.map((m) => <div key={m.id} className="rounded-xl p-4 bg-[#F9F8F6]"><div className="flex justify-between gap-3 text-xs text-[#4A554E]"><span>{m.sender_name} ← {m.recipient_name}</span><span>{new Date(m.created_at).toLocaleString("ar-SA")}</span></div>{m.body && <p className="mt-2 whitespace-pre-wrap text-sm">{m.body}</p>}{m.attachments?.map((a) => <a key={a.name} href={a.data} download={a.name} className="text-xs text-[#3E6A56] block mt-2"><Paperclip className="inline w-3 h-3 me-1" />{a.name}</a>)}</div>)}</div></div>
      </div>
      {user?.role !== "general_manager" && <div className="bg-white rounded-2xl border border-[#e6e2d8] p-5 space-y-3">
        <div>
          <h2 className="font-bold text-lg mb-1">تواصل واتساب</h2>
          <p className="text-xs text-[#4A554E]">اكتب نص الرسالة، ثم اختر جهة الاتصال. يفتح واتساب في التبويب نفسه؛ اختر المتابعة إلى التطبيق أو واتساب ويب، ثم اضغط إرسال داخل واتساب لإتمام الإرسال.</p>
        </div>
        <Textarea value={whatsappBody} onChange={(e) => setWhatsappBody(e.target.value)} placeholder="اكتب رسالة واتساب..." className="min-h-24 rounded-xl" />
        {user?.role === "principal" && <p className="text-xs text-[#4A554E]">التواصل متاح مع مسؤول الحساب والمشرف الإرشادي المخوّل ومرشدي مدرستك وقسمك فقط.</p>}
        {user?.role === "counselor" && <p className="text-xs text-[#4A554E]">جهات التواصل المتاحة: مدير المدرسة، مسؤول النظام، والمشرف الإرشادي المخوّل فقط.</p>}
        {whatsappLoading && <p>جاري تحميل جهات واتساب المسموحة...</p>}
        {whatsappError && <p role="alert" className="text-red-700">{whatsappError}</p>}
        {["principal", "counselor"].includes(user?.role) && !whatsappLoading && !whatsappError && !whatsappContacts.length && <p>لا توجد جهات تواصل متاحة ضمن نطاقك.</p>}
        <div className="flex flex-wrap gap-2">
          {whatsappContacts.map((contact) => {
            const href = whatsappHref(contact.phone, whatsappBody);
            if (!href) return <span key={contact.id || contact.name} className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-700">{contact.name} — {contact.phone ? "رقم التواصل غير صالح لواتساب" : "لا يوجد رقم واتساب مسجل"}؛ {canManageContacts ? "صححه في دليل التواصل." : "يرجى مراجعة مسؤول الحساب."}</span>;
            return (
              <a key={contact.id || contact.name} href={href} target="_self" rel="noreferrer" aria-label={`فتح واتساب — ${contact.name}`} className="inline-flex items-center gap-2 rounded-xl border border-[#3E6A56]/20 px-3 py-2 text-sm text-[#3E6A56]">
                <ExternalLink className="w-4 h-4" />{contact.name}{contact.communication_role ? ` — ${contact.communication_role}` : ""}{contact.phone ? ` (${contact.phone})` : ""}
              </a>
            );
          })}
        </div>
      </div>}
      {(canManageContacts || user?.role === "counselor") && <div className="bg-white rounded-2xl border border-[#e6e2d8] p-5 space-y-4">
        <div>
          <h2 className="font-bold text-lg mb-1">تواصل عبر البريد الإلكتروني</h2>
          <p className="text-xs text-[#4A554E]">اختر Gmail أو Outlook لفتح مسودة جاهزة بعنوان البريد ونصه. راجع الرسالة ثم اضغط إرسال داخل خدمة البريد.</p>
          {user?.role === "counselor" && <p className="text-xs text-[#4A554E]">البريد الإلكتروني متاح لمدير المدرسة ومسؤول النظام والمشرف الإرشادي المخوّل فقط.</p>}
        </div>
        <div>
          <Label className="mb-1.5 block">إلى</Label>
          <Select value={emailRecipient} onValueChange={setEmailRecipient}>
            <SelectTrigger className="rounded-xl"><SelectValue placeholder="اختر جهة التواصل" /></SelectTrigger>
            <SelectContent>
              {directory.filter((contact) => contact.email).map((contact) => (
                <SelectItem key={contact.id} value={contact.id}>{contact.name} — {contact.communication_role ? `${contact.communication_role} — ` : ""}{contact.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user?.role === "counselor" && !directory.some((contact) => contact.email) && (
            <p className="mt-2 text-xs text-[#4A554E]">لا توجد عناوين بريد إلكتروني مسجلة لجهات التواصل المسموحة.</p>
          )}
        </div>
        <Input value={emailSubject} onChange={(event) => setEmailSubject(event.target.value)} placeholder="موضوع الرسالة" className="rounded-xl" />
        <Textarea value={emailBody} onChange={(event) => setEmailBody(event.target.value)} placeholder="اكتب نص البريد..." className="min-h-24 rounded-xl" />
        <div className="flex flex-wrap gap-2">
          <a
            href={gmailHref || undefined}
            target="_self"
            aria-label="فتح مسودة البريد في Gmail"
            aria-disabled={!gmailHref}
            onClick={(event) => { if (!gmailHref) event.preventDefault(); }}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white ${gmailHref ? "bg-[#3E6A56] hover:bg-[#2D4F40]" : "cursor-not-allowed bg-gray-400"}`}
          >
            <Mail className="h-4 w-4" />فتح في Gmail
          </a>
          <a
            href={outlookHref || undefined}
            target="_self"
            aria-label="فتح مسودة البريد في Outlook"
            aria-disabled={!outlookHref}
            onClick={(event) => { if (!outlookHref) event.preventDefault(); }}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white ${outlookHref ? "bg-[#2563a5] hover:bg-[#1d4e81]" : "cursor-not-allowed bg-gray-400"}`}
          >
            <Mail className="h-4 w-4" />فتح في Outlook
          </a>
          <a
            href={mailtoHref || undefined}
            aria-label="فتح تطبيق البريد الافتراضي"
            aria-disabled={!mailtoHref}
            onClick={(event) => { if (!mailtoHref) event.preventDefault(); }}
            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm ${mailtoHref ? "border-[#3E6A56]/30 text-[#3E6A56] hover:bg-[#3E6A56]/5" : "cursor-not-allowed border-gray-300 text-gray-400"}`}
          >
            <Mail className="h-4 w-4" />تطبيق البريد الافتراضي
          </a>
        </div>
      </div>}
    </div>
  );
}
